import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { pollDepositResolution } from '@/lib/depositResolution';
// (BrandedPayButton stays copy-agnostic — it surfaces the raw error and the parent maps it.)

declare global {
  interface Window {
    paypal?: any;
  }
}

interface Props {
  fundingSource: 'paypal' | 'venmo';
  amount: number;
  leagueId: string;
  callEdgeFunction: (name: string, body: any) => Promise<any>;
  onSuccess: () => void;
  // SAB-605: receives the REAL error (carrying the edge fn's `.code` — GEO_BLOCKED,
  // KYC_REQUIRED, …), not a pre-baked string, so the parent routes it via
  // mapDepositError (location message, verify-identity CTA, etc.).
  onError: (err: unknown) => void;
  /**
   * SAB-1093: called when a capture response was inconclusive and we are polling for the
   * real outcome. The parent should show "processing", NOT failure — the webhook credits
   * correctly in this window, and a false failure is what causes double-charge retries.
   */
  onPending?: () => void;
  /**
   * SAB-570: parent-supplied fresh-geo-session provider. When present, createOrder
   * acquires a fresh Locance session and attaches it as geo_session_id so the SERVER
   * re-verifies the deposit; a failed/blocked gather aborts the order. Omitted (or the
   * parent's GEO off) → resolves ok with no session and the flow is unchanged.
   */
  acquireGeoSession?: () => Promise<{ ok: boolean; sessionId?: string }>;
}

/* ------------------------------------------------------------------ */
/*  Brand marks — official PP monogram + Venmo V on Venmo Blue tile.   */
/*  Sized 22×22 inside a 48px button per PayPal clear-space rules.     */
/* ------------------------------------------------------------------ */

function PayPalMark() {
  return (
    <svg
      viewBox="0 0 48 48"
      width="22"
      height="22"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        fill="#253B80"
        d="M18.7 13.5h6.4c3.4 0 5.7 1.7 5.3 5.4-.5 4.4-3.5 6.3-7 6.3h-2.7c-.5 0-.9.3-.9.8l-1.4 8.3c-.1.4-.4.7-.8.7h-3.5c-.5 0-.8-.4-.7-.9l3.4-19.8c.1-.4.4-.8.9-.8z"
      />
      <path
        fill="#179BD7"
        d="M28.1 20.2c-.6 3.7-3.6 5.5-7 5.5h-2.6c-.5 0-.9.3-.9.8l-1.3 7.7-.2 1.4c-.1.4.2.8.7.8h2.9c.4 0 .8-.3.9-.7l.1-.2 1-5.6.1-.3c.1-.4.5-.7.9-.7h.6c3.7 0 6.6-1.6 7.5-6 .4-1.8.2-3.4-.9-4.5-.3-.2-.6-.4-1.1-.5-.4 0-.5 1.2-.7 2.3z"
      />
    </svg>
  );
}

function VenmoMark() {
  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center"
      style={{
        width: 22,
        height: 22,
        borderRadius: 5,
        backgroundColor: '#008CFF',
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff">
        <path d="M19.5 4.2c.7 1.2 1 2.4 1 3.9 0 4.7-4 10.9-7.3 15.3H5.7L2.7 4.6l6.5-.6 1.6 12.7c1.5-2.4 3.3-6.2 3.3-8.8 0-1.4-.2-2.4-.6-3.2l5-.5z" />
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  BrandedPayButton                                                   */
/*                                                                     */
/*  Two stacked layers:                                                */
/*    • Visual layer (z-10, pointer-events:none): our design-system    */
/*      button — dark card bg, our radius, our typography. Cosmetic.   */
/*    • Functional layer (z-0, opacity-0): the real PayPal SDK button. */
/*      Receives the actual click via the visual layer's pointer-events*/
/*      passthrough — keeps the checkout flow untouched & brand-       */
/*      compliant (PayPal still owns the click target DOM).            */
/* ------------------------------------------------------------------ */

export function BrandedPayButton({
  fundingSource,
  amount,
  leagueId,
  callEdgeFunction,
  onSuccess,
  onError,
  onPending,
  acquireGeoSession,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [hovering, setHovering] = useState(false);
  const sdkMountRef = useRef<HTMLDivElement>(null);
  const { atLeast } = useNativeAppVersion();

  // SAB-521: the SDK Buttons instance is mounted once PER funding source (the effect
  // below is keyed on fundingSource, and the call site also passes key={fundingSource})
  // and deliberately NOT re-rendered when `amount` changes — re-rendering re-inits the
  // PayPal iframe and flickers. Its createOrder/onApprove/onError callbacks therefore
  // capture the MOUNT-TIME closure, which kept sending the initial amount (0) even after
  // the user picked $25. Keep a ref that always holds the latest props those callbacks
  // need, refreshed on every render, and read latestRef.current inside them so the live
  // amount (etc.) is used, not the stale captured value. No re-render on amount, no flicker.
  const latestRef = useRef({ amount, leagueId, callEdgeFunction, onSuccess, onError, onPending, acquireGeoSession });
  latestRef.current = { amount, leagueId, callEdgeFunction, onSuccess, onError, onPending, acquireGeoSession };

  // SAB-605: the PayPal SDK wraps (and loses the `.code` on) an error thrown from
  // createOrder before it reaches onError. Stash the real one here so onError can
  // surface the actual reason instead of a hardcoded "Payment failed" string.
  const lastCreateOrderErrorRef = useRef<unknown>(null);

  useEffect(() => {
    // Native uses the redirect flow (handleNativePress below), NEVER the SDK popup:
    // its window.open cannot be serviced by WKWebView, so the overlay renders but
    // cannot be dismissed (the paid-but-stuck sheet). Skip the SDK mount entirely.
    if (Capacitor.isNativePlatform()) return;
    if (!sdkMountRef.current) return;
    if (!window.paypal?.Buttons || !window.paypal?.FUNDING) return;

    const funding = fundingSource === 'venmo'
      ? window.paypal.FUNDING.VENMO
      : window.paypal.FUNDING.PAYPAL;

    const buttons = window.paypal.Buttons({
      fundingSource: funding,
      // SDK button styling is irrelevant to the user (it's invisible) but we
      // keep it valid so isEligible() / popup flow behave identically.
      style: {
        shape: 'rect',
        height: 48,
        color: fundingSource === 'paypal' ? 'black' : undefined,
        label: fundingSource === 'paypal' ? 'pay' : undefined,
      },
      onClick: () => {
        // Fires the instant the user clicks (before createOrder resolves).
        setLoading(true);
      },
      createOrder: async () => {
        // Read the live values, not the mount-time closure (SAB-521): `amount`
        // was captured as 0 at mount and never updated.
        const { amount, leagueId, callEdgeFunction, acquireGeoSession } = latestRef.current;
        try {
          // SAB-570: fresh geo session for this PayPal deposit. A failed/blocked gather
          // aborts the order (parent surfaces the block); server stays the backstop.
          let geoSessionId: string | undefined;
          if (acquireGeoSession) {
            const geoSess = await acquireGeoSession();
            if (!geoSess.ok) {
              // Client-side geo block (enforce client): tag it so the parent maps it
              // to the location message, same as the server's GEO_BLOCKED.
              const e = new Error('We could not verify your location for this deposit.') as Error & { code?: string };
              e.code = 'GEO_BLOCKED';
              throw e;
            }
            geoSessionId = geoSess.sessionId;
          }
          const res = await callEdgeFunction('paypal-create-order', {
            amount,
            leagueId,
            ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
          });
          return res.orderId;
        } catch (e) {
          // SAB-605: preserve the real error (with .code) for onError before the SDK
          // wraps it, then re-throw to abort the order.
          lastCreateOrderErrorRef.current = e;
          throw e;
        }
      },
      onApprove: async (data: any) => {
        const { leagueId, callEdgeFunction, onSuccess, onError, onPending } = latestRef.current;
        const orderId = data.orderID;
        // SAB-1093: a non-success capture response is NOT evidence the deposit failed.
        // paypal-webhook races this call and often wins; it credits correctly either way.
        // Before reporting failure, ask the row what actually happened. Reporting a false
        // failure is what makes a user retry, and a retry past SAB-1090's 15-minute dedupe
        // window is a second real charge.
        const resolveBeforeFailing = async (originalErr: unknown) => {
          onPending?.();
          const outcome = await pollDepositResolution(orderId);
          if (outcome === 'completed') {
            onSuccess();
            return;
          }
          if (outcome === 'failed') {
            onError(originalErr);
            return;
          }
          // UNRESOLVED: we genuinely cannot tell. Never claim money did or did not move —
          // the webhook is still the source of truth and may credit after this.
          onError(new Error(
            "We couldn't confirm your deposit. If your payment went through, your balance " +
            'updates automatically once it settles — please check before trying again.',
          ));
        };
        try {
          const res = await callEdgeFunction('paypal-capture-order', {
            orderId,
            leagueId,
          });
          if (res.success) {
            onSuccess();
          } else if (res.code === 'DEPOSIT_TERMINAL') {
            // The server read a terminal row. That IS an answer — no need to poll.
            onError(res.error);
          } else {
            await resolveBeforeFailing(res.error || 'Payment capture failed');
          }
        } catch (err: any) {
          await resolveBeforeFailing(err);
        } finally {
          setLoading(false);
        }
      },
      // SAB-580 (deferred, NOT in this PR): neither onCancel nor onError cleans up the
      // pending wallet_transactions row that paypal-create-order inserted, so every
      // abandoned checkout leaves an orphan (9 already on wallet 6df4b177 as of
      // 2026-07-29). To land under SAB-580: (1) a paypal-cancel-order edge action wired
      // here on BOTH onCancel and onError to cancel that pending row by order id;
      // (2) a scheduled sweep to expire stale pending PayPal rows for the tab-close /
      // browser-crash cases where no callback fires; (3) backfill the existing orphaned
      // prod rows; (4) audit whether the pending_deposits column ($60.00 on that wallet)
      // is accurate. Note paypal-create-order never touches pending_deposits, so these
      // orphans are transaction-history litter, not inflated pending balance.
      onCancel: () => setLoading(false),
      onError: (sdkErr: any) => {
        console.error(`[${fundingSource}] button error:`, sdkErr);
        setLoading(false);
        // SAB-605: prefer the real createOrder error (carries the edge `.code` —
        // GEO_BLOCKED, KYC_REQUIRED, MIN_DEPOSIT, …). Fall back to the SDK error for
        // genuine PayPal-side failures (popup closed, capture network), which the
        // parent's mapper renders as the retry message.
        const err = lastCreateOrderErrorRef.current ?? sdkErr;
        lastCreateOrderErrorRef.current = null;
        latestRef.current.onError(err);
      },
    });

    // Only mark rendered when isEligible() passed AND render() actually ran. On LIVE,
    // Venmo returns isEligible()===false, so the instance is created but never
    // rendered; calling close() on a never-rendered instance makes the SDK invoke
    // close handlers that were never wired ("P.onClose is not a function").
    let rendered = false;

    if (buttons.isEligible()) {
      rendered = true;
      // render() returns a promise; if a fast unmount/remount (a key={fundingSource}
      // switch or the 1024px breakpoint panel swap) interrupts it, swallow the async
      // rejection so it never reaches the ErrorBoundary / Sentry.
      const renderResult = buttons.render(sdkMountRef.current) as unknown;
      if (renderResult && typeof (renderResult as PromiseLike<unknown>).then === 'function') {
        (renderResult as Promise<unknown>).catch(() => { /* render interrupted by teardown */ });
      }
    }

    // Teardown so switching funding source (key={fundingSource} + the [fundingSource]
    // dep both remount) or unmounting closes the SDK button, instead of leaving a
    // stale PayPal/Venmo iframe behind. Two SPECIFIC guards, not a blanket swallow:
    //   1. NEVER close an instance we did not render (the ineligible-Venmo case above,
    //      which is the actual prod bug this fixes).
    //   2. close() returns a zoid promise that rejects ASYNCHRONOUSLY, so a sync
    //      try/catch cannot catch it (that async reject is the setTimeout-mechanism
    //      Sentry error). Attach .catch(); keep the try/catch for a sync throw.
    return () => {
      if (!rendered) return;
      try {
        const closeResult = buttons.close?.() as unknown;
        if (closeResult && typeof (closeResult as PromiseLike<unknown>).then === 'function') {
          (closeResult as Promise<unknown>).catch(() => { /* already torn down */ });
        }
      } catch { /* sync teardown throw */ }
    };
  }, [fundingSource]);

  // Native redirect flow (no SDK popup). Create the order server-side, then open
  // PayPal's approval URL in the system browser via @capacitor/browser. PayPal
  // returns to /paypal-return -> app custom scheme -> NativeUrlHandler captures the
  // order and navigates. Reads latestRef so the live amount is used (SAB-521).
  const handleNativePress = async () => {
    if (loading) return;
    setLoading(true);
    const { amount, leagueId, callEdgeFunction, acquireGeoSession, onError } = latestRef.current;
    try {
      let geoSessionId: string | undefined;
      if (acquireGeoSession) {
        const geoSess = await acquireGeoSession();
        if (!geoSess.ok) { setLoading(false); return; } // parent surfaces the geo block
        geoSessionId = geoSess.sessionId;
      }
      const res = await callEdgeFunction('paypal-create-order', {
        amount,
        leagueId,
        ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
      });
      if (!res?.approveUrl) {
        setLoading(false);
        onError('Could not start PayPal checkout. Please try again.');
        return;
      }
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: res.approveUrl });
      // Leave `loading` set: the user is now in the system browser. On approval the
      // appUrlOpen handler captures and navigates to /wallet (unmounting this); on
      // cancel it returns to /wallet/deposit and this remounts fresh (loading reset).
    } catch (err: any) {
      // SAB-605: surface the real error (with .code) so the parent maps it — geo
      // block → location message, KYC → verify CTA — not a hardcoded string.
      setLoading(false);
      onError(err);
    }
  };

  const Mark = fundingSource === 'paypal' ? PayPalMark : VenmoMark;
  const ariaLabel = fundingSource === 'paypal' ? 'Pay with PayPal' : 'Pay with Venmo';

  // SAB (native PayPal/Venmo gate): the PayPal SDK uses window.open, which
  // Capacitor's WKWebView cannot service — the web content process dies before any
  // API call. Render nothing on native so no PayPal/Venmo button can be reached
  // from ANY call site. Display gate only (no code removed); reversed once
  // server-side order creation + @capacitor/browser + deep-link return lands.
  // Placed after all hooks so the hook order is unchanged (rules-of-hooks safe);
  // the mount effect no-ops because its sdkMountRef never renders.
  // Build 6 Phase A: version-aware. Native builds below 1.2.0 lack the
  // @capacitor/browser + appUrlOpen approval flow, so keep them gated; native 1.2.0+
  // and all web render. Fail-safe gated while App.getInfo() is unresolved.
  if (Capacitor.isNativePlatform() && !atLeast('1.2.0')) return null;

  // Native 1.2.0+ : our own real button drives the redirect flow. The web SDK's
  // two-layer (cosmetic + invisible-iframe) approach is NOT used here, so there is no
  // popup to get stuck. Same PayPal-branded look as the web cosmetic layer.
  if (Capacitor.isNativePlatform()) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        aria-busy={loading}
        disabled={loading}
        onClick={handleNativePress}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={cn(
          'relative h-12 w-full flex items-center justify-center gap-2.5',
          'rounded-md border transition-all duration-150',
          hovering && !loading ? 'bg-[#232B40] border-[#3a4360]' : 'bg-[#1E2536] border-[#2a3040]',
          loading && 'pointer-events-none',
        )}
      >
        <Mark />
        {loading ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Connecting…
          </span>
        ) : fundingSource === 'paypal' ? (
          <span className="text-sm font-semibold">
            <span style={{ color: '#fff' }}>Pay</span>
            <span style={{ color: '#5BC0F8' }}>Pal</span>
          </span>
        ) : (
          <span className="text-sm font-semibold text-white">Venmo</span>
        )}
      </button>
    );
  }

  return (
    <div
      role="button"
      aria-label={ariaLabel}
      aria-busy={loading}
      className={cn(
        'relative h-12 w-full',
        loading && 'pointer-events-none',
      )}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* ---------- VISUAL LAYER (cosmetic, no clicks) ---------- */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute inset-0 z-10 pointer-events-none',
          'flex items-center justify-center gap-2.5',
          'rounded-md border transition-all duration-150',
          hovering && !loading
            ? 'bg-[#232B40] border-[#3a4360]'
            : 'bg-[#1E2536] border-[#2a3040]',
        )}
      >
        <Mark />
        {loading ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Connecting…
          </span>
        ) : fundingSource === 'paypal' ? (
          <span className="text-sm font-semibold">
            <span style={{ color: '#fff' }}>Pay</span>
            <span style={{ color: '#5BC0F8' }}>Pal</span>
          </span>
        ) : (
          <span className="text-sm font-semibold text-white">Venmo</span>
        )}
      </div>

      {/* ---------- FUNCTIONAL LAYER (invisible SDK button) ---------- */}
      {/*
        Clicks pass through the visual layer (pointer-events: none) and
        land here, on PayPal's own iframe. createOrder/onApprove/onError
        on the SDK Buttons() instance handle the checkout exactly as
        before — we do not call any payment code ourselves.
      */}
      <div
        ref={sdkMountRef}
        className="absolute inset-0 z-0 opacity-0"
      />
    </div>
  );
}
